import React, { useEffect, useState } from 'react';
import { List, Table, Button, Modal, Spin, notification, Typography, Space, Tabs, Tag } from '@pankod/refine-antd';
import { IResourceComponentsProps } from '@pankod/refine-core';
import axiosService from 'services/axios.service';
import { formatDateTime } from 'utils';

const { Text } = Typography;
const { TabPane } = Tabs;

export const SubscriptionReconciliationList: React.FC<IResourceComponentsProps> = () => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<any[]>([]);
    const [multiActiveData, setMultiActiveData] = useState<any[]>([]);
    const [syncModalVisible, setSyncModalVisible] = useState(false);
    const [syncLoading, setSyncLoading] = useState(false);
    const [selectedSub, setSelectedSub] = useState<any>(null);
    const [paypalData, setPaypalData] = useState<any>(null);
    const [hoveredTransId, setHoveredTransId] = useState<string | null>(null);

    const fetchDiscrepancies = async () => {
        setLoading(true);
        try {
            const [discrepanciesRes, multiActiveRes] = await Promise.all([
                axiosService.get('/v1/admin/subscription-transactions/reconcile/discrepancies'),
                axiosService.get('/v1/admin/subscription-transactions/reconcile/multi-active')
            ]);
            setData(discrepanciesRes.data.data);
            setMultiActiveData(multiActiveRes.data.data);
        } catch (error: any) {
            notification.error({
                message: 'Error fetching discrepancies',
                description: error.message || 'Something went wrong',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSync = async (paypalSubscriptionId: string) => {
        setSyncLoading(true);
        setPaypalData(null);
        setSelectedSub(paypalSubscriptionId);
        setSyncModalVisible(true);
        try {
            const response = await axiosService.get(`/v1/admin/subscription-transactions/reconcile/sync/${paypalSubscriptionId}`);
            setPaypalData(response.data.data);
        } catch (error: any) {
            notification.error({
                message: 'Error syncing with PayPal',
                description: error.message || 'Something went wrong',
            });
        } finally {
            setSyncLoading(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (!id) {
            notification.warning({ message: 'Invalid Transaction ID' });
            return;
        }
        try {
            await axiosService.delete(`/v1/admin/subscription-transactions/${id}`);
            notification.success({ message: 'Transaction deleted successfully' });
            // Refresh local data after delete
            if (selectedSub) {
                const response = await axiosService.get(`/v1/admin/subscription-transactions/reconcile/sync/${selectedSub}`);
                setPaypalData(response.data.data);
            }
            fetchDiscrepancies();
        } catch (error: any) {
            notification.error({
                message: 'Error deleting transaction',
                description: error.message || 'Something went wrong',
            });
        }
    };

    useEffect(() => {
        fetchDiscrepancies();
    }, []);

    const discrepancyColumns = [
        {
            title: 'User',
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: 'PayPal Sub ID',
            key: 'subId',
            render: (_: any, record: any) => record._id.paypalSubscriptionId
        },
        {
            title: 'Record Count',
            dataIndex: 'count',
            key: 'count',
            render: (count: number) => <Tag color="orange">{count} records</Tag>
        },
        {
            title: 'Action',
            key: 'action',
            render: (_: any, record: any) => (
                <Button
                    type='primary'
                    onClick={() => handleSync(record._id.paypalSubscriptionId)}
                >
                    Investigate Cycles
                </Button>
            ),
        },
    ];

    const multiActiveColumns = [
        {
            title: 'User',
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: 'Active Subscription IDs',
            dataIndex: 'activeSubIds',
            key: 'activeSubIds',
            render: (ids: string[]) => (
                <Space direction="vertical" size={2}>
                    {ids.map(id => (
                        <Tag key={id} color="blue">{id}</Tag>
                    ))}
                </Space>
            )
        },
        {
            title: 'Count',
            dataIndex: 'subCount',
            key: 'subCount',
            render: (count: number) => <Tag color="red">{count} Active Subs</Tag>
        },
        {
            title: 'Action',
            key: 'action',
            render: (_: any, record: any) => (
                <Space>
                    {record.activeSubIds.map((id: string) => (
                        <Button key={id} size="small" onClick={() => handleSync(id)}>
                            Sync {id.slice(-4)}
                        </Button>
                    ))}
                </Space>
            ),
        },
    ];

    return (
        <List title='Subscription Reconciliation'>
            <Tabs defaultActiveKey="1" type="card">
                <TabPane tab={`Multi-Active Subscriptions (${multiActiveData.length})`} key="1">
                    <Table
                        dataSource={multiActiveData}
                        columns={multiActiveColumns}
                        loading={loading}
                        rowKey="_id"
                    />
                </TabPane>
                <TabPane tab={`Cross-Cycle Duplicates (${data.length})`} key="2">
                    <Table
                        dataSource={data}
                        columns={discrepancyColumns}
                        loading={loading}
                        rowKey={(record) => `${record._id.paypalSubscriptionId}-${record._id.dateStart}`}
                    />
                </TabPane>
            </Tabs>

            <Modal
                title={`Cycle Inspection: ${selectedSub}`}
                visible={syncModalVisible}
                onCancel={() => setSyncModalVisible(false)}
                width={1000}
                footer={[
                    <Button key='back' onClick={() => setSyncModalVisible(false)}>
                        Close
                    </Button>,
                ]}
            >
                {syncLoading ? (
                    <div style={{ textAlign: 'center', padding: '50px' }}>
                        <Spin size='large' />
                    </div>
                ) : (
                    <div>
                        <div style={{ marginBottom: '20px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                <Text strong>DB Records (Local Data):</Text>
                                <Space>
                                    <Text type="secondary" style={{ fontSize: '12px' }}>Green = Matched</Text>
                                    <Text type="secondary" style={{ fontSize: '12px' }}>Red = Ghost/Duplicate</Text>
                                </Space>
                            </div>
                            <Table
                                dataSource={paypalData?.dbRecords || []}
                                pagination={false}
                                size='small'
                                rowKey="_id"
                                onRow={(record) => ({
                                    onMouseEnter: () => setHoveredTransId(record.paypalPayeeTransactionId),
                                    onMouseLeave: () => setHoveredTransId(null),
                                })}
                                rowClassName={(record) => {
                                    const matches = paypalData?.paypalTransactions?.filter((p: any) => p.id === record.paypalPayeeTransactionId);
                                    const hasMatch = matches && matches.length > 0;
                                    const isDuplicate = (paypalData?.dbRecords?.filter((db: any) => db.paypalPayeeTransactionId === record.paypalPayeeTransactionId)?.length || 0) > 1;

                                    if (hoveredTransId && record.paypalPayeeTransactionId === hoveredTransId) return 'ant-table-row-selected';
                                    if (!hasMatch || isDuplicate) return 'reconcile-mismatch';
                                    return 'reconcile-match';
                                }}
                                columns={[
                                    {
                                        title: 'Status',
                                        key: 'match',
                                        render: (_: any, record: any) => {
                                            const hasMatch = paypalData?.paypalTransactions?.some((p: any) => p.id === record.paypalPayeeTransactionId);
                                            const dbOccurrences = paypalData?.dbRecords?.filter((db: any) => db.paypalPayeeTransactionId === record.paypalPayeeTransactionId)?.length || 0;

                                            if (!hasMatch) return <Text type="danger" strong>MISSING IN PAYPAL</Text>;
                                            if (dbOccurrences > 1) return <Text type="warning" strong>DUPLICATE ENTRY</Text>;
                                            return <Text type="success">✓ Matched</Text>;
                                        }
                                    },
                                    { title: 'Trans ID', dataIndex: 'paypalPayeeTransactionId' },
                                    { title: 'Start', dataIndex: 'dateStart', render: (val) => formatDateTime(val) },
                                    { title: 'End', dataIndex: 'dateEnd', render: (val) => formatDateTime(val) },
                                    { title: 'Cost', dataIndex: 'cost' },
                                    {
                                        title: 'Action',
                                        render: (_: any, record: any) => (
                                            <Button danger size='small' onClick={() => handleDelete(record._id)}>
                                                Delete Duplicate
                                            </Button>
                                        ),
                                    },
                                ]}
                            />
                        </div>
                        <div>
                            <Text strong>PayPal Official Transactions (Source of Truth):</Text>
                            <Table
                                dataSource={paypalData?.paypalTransactions || []}
                                pagination={false}
                                size='small'
                                rowKey="id"
                                onRow={(record) => ({
                                    onMouseEnter: () => setHoveredTransId(record.id),
                                    onMouseLeave: () => setHoveredTransId(null),
                                })}
                                rowClassName={(record) => {
                                    const hasDbMatch = paypalData?.dbRecords?.some((db: any) => db.paypalPayeeTransactionId === record.id);
                                    if (hoveredTransId && record.id === hoveredTransId) return 'ant-table-row-selected';
                                    return hasDbMatch ? 'reconcile-match' : 'reconcile-warning';
                                }}
                                columns={[
                                    {
                                        title: 'Recorded?',
                                        key: 'recorded',
                                        render: (_: any, record: any) => {
                                            const hasDbMatch = paypalData?.dbRecords?.some((db: any) => db.paypalPayeeTransactionId === record.id);
                                            return hasDbMatch ? <Text type="success">✓ Recorded</Text> : <Text type="warning" strong>NOT IN DB</Text>;
                                        }
                                    },
                                    { title: 'ID', dataIndex: 'id' },
                                    { title: 'Time', dataIndex: 'time', render: (val) => formatDateTime(val) },
                                    { title: 'Status', dataIndex: 'status' },
                                    { title: 'Amount', dataIndex: 'amount_with_breakdown', render: (val) => val?.gross_amount?.value },
                                ]}
                            />
                        </div>
                        <style>{`
                            .reconcile-match { background-color: #f6ffed; transition: all 0.3s ease; }
                            .reconcile-mismatch { background-color: #fff1f0; transition: all 0.3s ease; }
                            .reconcile-warning { background-color: #fffbe6; transition: all 0.3s ease; }
                            .ant-table-row-selected { background-color: #bae7ff !important; transition: all 0.2s ease; }
                            .ant-table-row:hover { cursor: crosshair; }
                        `}</style>
                    </div>
                )}
            </Modal>
        </List>
    );
};
